// src/pages/ProgramDetail.tsx
import React, { useState } from 'react';
import { Link, useParams, Navigate } from 'react-router-dom';
import SEO, { breadcrumbLd, faqLd, govServiceLd, articleLd } from '../components/common/SEO';
import Breadcrumb from '../components/common/Breadcrumb';
import Sidebar from '../components/common/Sidebar';
import ProgramCard from '../components/common/ProgramCard';
import InContentAd from '../components/ads/InContentAd';
import CoupangPicks from '../components/affiliate/CoupangPicks';
import CoupangBanner from '../components/affiliate/CoupangBanner';
import Icon from '../components/common/Icon';
import ProgramPhoto from '../components/common/ProgramPhoto';
import ProgramVideo from '../components/common/ProgramVideo';
import { getProgramBySlug, getRelatedPrograms } from '../data/supportPrograms';
import { getCategory } from '../data/categories';

const ProgramDetail: React.FC = () => {
    const { slug = '' } = useParams();
    const program = getProgramBySlug(slug);
    const [openFaq, setOpenFaq] = useState<number | null>(0);

    if (!program) return <Navigate to="/404" replace />;

    const cat = getCategory(program.category);
    const related = getRelatedPrograms(program);
    const path = `/program/${program.slug}`;

    const crumbs = [
        { name: '홈', path: '/' },
        { name: cat.name, path: cat.path },
        { name: program.title, path },
    ];

    const ld: object[] = [
        breadcrumbLd(crumbs),
        govServiceLd({
            title: program.title,
            description: program.summary,
            path,
            agency: program.agency,
            amount: program.amount,
            eligibility: program.eligibility,
            updated: program.updated,
        }),
        articleLd({
            title: `${program.title} 신청자격·지원금액·신청방법 (2026년)`,
            description: program.summary,
            path,
            updated: program.updated,
        }),
    ];
    if (program.faq?.length) ld.push(faqLd(program.faq));

    return (
        <>
            <SEO
                path={path}
                title={`${program.title} 신청자격·지원금액·신청방법 (2026년 최신)`}
                description={`${program.summary} 지원금액 ${program.amount}. 지원대상, 자격요건, 신청기간, 준비서류와 신청 절차를 2026년 기준으로 정리했습니다.`}
                keywords={[...program.tags, program.title, `${program.title} 신청방법`, `${program.title} 자격`].join(', ')}
                updated={program.updated}
                jsonLd={ld}
            />

            <div className="gh-layout">
                <div>
                    <Breadcrumb items={crumbs} />

                    <article className="gh-detail">
                        <header className="gh-detail__head">
                            <div className="gh-detail__headtext">
                                <div className="gh-card__top">
                                    <span className="gh-card__cat">{cat.shortName}</span>
                                    {program.hot && (
                                        <span className="gh-card__flag gh-card__flag--hot">인기</span>
                                    )}
                                    {program.isNew && (
                                        <span className="gh-card__flag gh-card__flag--new">2026 변경</span>
                                    )}
                                </div>

                                <h1 className="gh-detail__title">{program.title}</h1>
                                <p className="gh-detail__summary">{program.summary}</p>

                                <div className="gh-detail__meta">
                                    <span>
                                        <Icon name="building" size={14} />
                                        {program.agency}
                                    </span>
                                    <span>
                                        <Icon name="clock" size={14} />
                                        최종 확인 {program.updated}
                                    </span>
                                    <span>
                                        <Icon name="eye" size={14} />
                                        조회 {program.views.toLocaleString()}
                                    </span>
                                </div>
                            </div>

                            <div className="gh-detail__art">
                                <ProgramPhoto
                                    category={program.category}
                                    slug={program.slug}
                                    className="gh-detail__photo"
                                />
                            </div>
                        </header>

                        <div className="gh-keyfacts">
                            <div className="gh-keyfact">
                                <div className="gh-keyfact__k">지원금액</div>
                                <div className="gh-keyfact__v gh-keyfact__v--accent">{program.amount}</div>
                            </div>
                            <div className="gh-keyfact">
                                <div className="gh-keyfact__k">지원대상</div>
                                <div className="gh-keyfact__v">{program.eligibility}</div>
                            </div>
                            <div className="gh-keyfact">
                                <div className="gh-keyfact__k">신청기간</div>
                                <div className="gh-keyfact__v">{program.applicationPeriod}</div>
                            </div>
                            <div className="gh-keyfact">
                                <div className="gh-keyfact__k">신청방법</div>
                                <div className="gh-keyfact__v">{program.applicationMethod}</div>
                            </div>
                        </div>

                        <div className="gh-detail__body">
                            <section className="gh-block">
                                <h2 className="gh-block__title">{program.title}이란?</h2>
                                <p>{program.description}</p>
                            </section>

                            {program.changes2026 && (
                                <div className="gh-callout">
                                    <Icon name="sparkle" size={20} />
                                    <div>
                                        <div className="gh-callout__title">2026년 달라진 점</div>
                                        <p className="gh-callout__text">{program.changes2026}</p>
                                    </div>
                                </div>
                            )}

                            <section className="gh-block">
                                <h2 className="gh-block__title">신청 자격 요건</h2>
                                <ul className="gh-list">
                                    {program.requirements.map((r, i) => (
                                        <li key={i}>
                                            <Icon name="check" size={16} strokeWidth={2.4} />
                                            <span>{r}</span>
                                        </li>
                                    ))}
                                </ul>
                            </section>

                            {program.documents && program.documents.length > 0 && (
                                <section className="gh-block">
                                    <h2 className="gh-block__title">준비 서류</h2>
                                    <ul className="gh-list">
                                        {program.documents.map((d, i) => (
                                            <li key={i}>
                                                <Icon name="document" size={16} />
                                                <span>{d}</span>
                                            </li>
                                        ))}
                                    </ul>
                                </section>
                            )}

                            <InContentAd />

                            {program.steps && program.steps.length > 0 && (
                                <section className="gh-block">
                                    <h2 className="gh-block__title">신청 절차</h2>
                                    <ol className="gh-steps">
                                        {program.steps.map((s, i) => (
                                            <li key={i}>{s}</li>
                                        ))}
                                    </ol>
                                </section>
                            )}

                            <section className="gh-block">
                                <h2 className="gh-block__title">관련 영상</h2>
                                <ProgramVideo
                                    title={program.title}
                                    searchQuery={`${program.title} 신청방법 ${program.updated.slice(0, 4)}`}
                                />
                            </section>

                            {program.faq && program.faq.length > 0 && (
                                <section className="gh-block">
                                    <h2 className="gh-block__title">자주 묻는 질문</h2>
                                    <div className="gh-faq">
                                        {program.faq.map((f, i) => (
                                            <div
                                                className={`gh-faq__item${openFaq === i ? ' is-open' : ''}`}
                                                key={i}
                                            >
                                                <button
                                                    type="button"
                                                    className="gh-faq__q"
                                                    onClick={() => setOpenFaq(openFaq === i ? null : i)}
                                                    aria-expanded={openFaq === i}
                                                >
                                                    <span className="gh-faq__mark">Q</span>
                                                    {f.q}
                                                    <Icon name="chevronDown" size={16} strokeWidth={2.2} />
                                                </button>
                                                {openFaq === i && <p className="gh-faq__a">{f.a}</p>}
                                            </div>
                                        ))}
                                    </div>
                                </section>
                            )}

                            {program.outlook2027 && (
                                <div className="gh-callout gh-callout--emerald">
                                    <Icon name="chart" size={20} />
                                    <div>
                                        <div className="gh-callout__title">2027년 전망</div>
                                        <p className="gh-callout__text">
                                            {program.outlook2027}{' '}
                                            <Link to="/2027" style={{ color: 'var(--blue-700)', fontWeight: 700 }}>
                                                2027년 전망 전체 보기 →
                                            </Link>
                                        </p>
                                    </div>
                                </div>
                            )}

                            <div className="gh-callout gh-callout--amber">
                                <Icon name="shield" size={20} />
                                <div>
                                    <div className="gh-callout__title">신청 전 확인하세요</div>
                                    <p className="gh-callout__text">
                                        본 내용은 {program.updated} 기준으로 정리한 참고 정보입니다. 실제 지원
                                        자격과 지급액은 소득·재산 심사 결과에 따라 달라질 수 있으므로, 신청 전
                                        반드시 {program.agency}의 공식 공고를 확인하시기 바랍니다.
                                    </p>
                                </div>
                            </div>

                            <div className="gh-applybar">
                                <div className="gh-applybar__text">
                                    <div className="gh-applybar__title">{program.title} 신청하기</div>
                                    <div className="gh-applybar__desc">{program.applicationMethod}</div>
                                </div>
                                <a
                                    href={program.link}
                                    target="_blank"
                                    rel="noopener noreferrer"
                                    className="gh-apply"
                                >
                                    공식 사이트로 이동
                                    <Icon name="external" size={16} strokeWidth={2.1} />
                                </a>
                            </div>

                            <CoupangPicks slug={program.slug} category={program.category} />
                            <CoupangBanner linkSeed={program.slug} />
                        </div>
                    </article>

                    {related.length > 0 && (
                        <section className="gh-sec" style={{ marginTop: 26 }}>
                            <div className="gh-sec__head">
                                <div>
                                    <h2 className="gh-sec__title">
                                        <Icon name={cat.icon} size={19} />
                                        함께 보면 좋은 {cat.shortName} 지원금
                                    </h2>
                                </div>
                                <Link to={cat.path} className="gh-sec__more">
                                    전체 보기
                                    <Icon name="chevronRight" size={13} strokeWidth={2.4} />
                                </Link>
                            </div>
                            <div className="gh-cards">
                                {related.map(p => (
                                    <ProgramCard key={p.id} program={p} hideCategory />
                                ))}
                            </div>
                        </section>
                    )}
                </div>

                <Sidebar category={program.category} rankTitle={`${cat.shortName} 인기 지원금`} />
            </div>
        </>
    );
};

export default ProgramDetail;
